import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';

export interface CalendarLossPeriod {
    from: string;
    to: string;
}

function formatBoundary(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01 00:00:00`;
}

/** Calendar boundaries use local server time, like the source CSV. The end is exclusive. */
export function getCompletedMonthPeriod(months: number, now = new Date()): CalendarLossPeriod {
    return {
        from: formatBoundary(new Date(now.getFullYear(), now.getMonth() - months, 1)),
        to: formatBoundary(new Date(now.getFullYear(), now.getMonth(), 1)),
    };
}

interface CounterPoint {
    date: string;
    time: number;
    value: number;
}

function prepareCounterPoints(values: number[], dates: string[]): CounterPoint[] {
    return dates.map((date, index) => ({
        date,
        time: new Date(date.replace(' ', 'T')).getTime(),
        value: values[index],
    })).filter(({ time, value }) => Number.isFinite(time) && Number.isFinite(value))
        .sort((a, b) => a.time - b.time);
}

/** Use the first and last available readings strictly inside the requested calendar period. */
export function resolveAvailablePeriodLoss(values: number[], dates: string[], period: CalendarLossPeriod): {
    value: number;
    period: CalendarLossPeriod;
} | null {
    const start = new Date(period.from.replace(' ', 'T')).getTime();
    const end = new Date(period.to.replace(' ', 'T')).getTime();
    const points = prepareCounterPoints(values, dates).filter(({ time }) => time >= start && time < end);
    const first = points[0];
    const last = points.at(-1);
    if (!first || !last || last.time <= first.time) {
        return null;
    }
    return { value: last.value - first.value, period: { from: first.date, to: last.date } };
}

/** Use the nearest real reading within 14 calendar days; ties prefer the earlier reading. */
function findBoundaryPoint(points: CounterPoint[], time: number): CounterPoint | null {
    let low = 0;
    let high = points.length;
    while (low < high) {
        const middle = Math.floor((low + high) / 2);
        if (points[middle].time < time) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }

    const before = points[low - 1];
    const after = points[low];
    const closest = !before ? after : !after ? before
        : time - before.time <= after.time - time ? before : after;
    const earliest = new Date(time);
    const latest = new Date(time);
    earliest.setDate(earliest.getDate() - 14);
    latest.setDate(latest.getDate() + 14);
    return closest && closest.time >= earliest.getTime() && closest.time <= latest.getTime()
        ? closest
        : null;
}

function resolvePreparedCalendarLoss(points: CounterPoint[], period: CalendarLossPeriod): number | null {
    const start = new Date(period.from.replace(' ', 'T')).getTime();
    const end = new Date(period.to.replace(' ', 'T')).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || !points.some(({ time }) => time >= start && time < end)) {
        return null;
    }

    const first = findBoundaryPoint(points, start);
    const last = findBoundaryPoint(points, end);
    if (!first || !last || last.time < first.time || last.value < first.value) {
        return null;
    }
    // Check the actual representative interval, including readings outside the nominal month.
    const inside = points.filter(({ time }) => time >= first.time && time <= last.time);
    if (inside.some((point, index) => (
        index > 0 && point.value < inside[index - 1].value
    ))) {
        return null;
    }
    return last.value - first.value;
}

/** Subtract consistent boundary representatives; empty periods and counter resets remain missing. */
export function resolveCalendarLoss(values: number[], dates: string[], period: CalendarLossPeriod): number | null {
    return resolvePreparedCalendarLoss(prepareCounterPoints(values, dates), period);
}

export function createMonthlyLossSeries(data: ParsedTimeSeriesCsv, now = new Date(), locale = 'de-DE'): ParsedTimeSeriesCsv {
    const periods = Array.from({ length: 12 }, (_, index) => {
        const end = new Date(now.getFullYear(), now.getMonth() - 11 + index, 1);
        return getCompletedMonthPeriod(1, end);
    });
    return {
        ...data,
        plotXAxisValues: periods.map(({ from }) => {
            const date = new Date(from.replace(' ', 'T'));
            const month = date.toLocaleDateString(locale, { month: 'short' }).replace(/\./g, '').slice(0, 3);
            return `${month} ${date.getFullYear()}`;
        }),
        plotYAxisValues: Object.fromEntries(data.plotYAxisColumns.map((column) => {
            const points = prepareCounterPoints(data.plotYAxisValues[column] ?? [], data.plotXAxisValues);
            return [column, periods.map((period) => resolvePreparedCalendarLoss(points, period) ?? Number.NaN)];
        })),
    };
}
